<script setup lang="ts">
// DEV-ONLY floating panel to impersonate a shop by Customer ID.
// Mounted standalone (outside the app's Vue tree) by plugins/devStorePanel.client.ts,
// so it only touches localStorage + location.reload — no pinia/router coupling.
import { ref, computed } from "vue";
import {
  getDevStoreOverride,
  setDevStoreOverride,
  clearDevStoreOverride,
  getDevStoreBypass,
  setDevStoreBypass,
  isDevStoreBypassPair,
} from "~/utils/devStoreOverride";

const open = ref(false);
const input = ref("");
const activeOverride = ref(getDevStoreOverride());
const bypass = ref(getDevStoreBypass() ?? "");
const bypassError = ref(false);

// Real logged-in shop, read straight from the auth snapshot cache so the panel
// stays standalone (no store import). Best-effort; blank if unavailable.
const realStoreId = (() => {
  try {
    const raw = localStorage.getItem("authSnapshot");
    if (!raw) return "";
    return String(JSON.parse(raw)?.storeId ?? "");
  } catch {
    return "";
  }
})();

const effectiveStoreId = computed(() => activeOverride.value || realStoreId);
const isImpersonating = computed(() => !!activeOverride.value);

function apply() {
  const id = input.value.trim();
  if (!id) return;
  // Without the pair the server 401s every request (the session is synthetic,
  // there is no token) — don't switch into a shop that cannot load.
  if (!isDevStoreBypassPair(bypass.value)) {
    bypassError.value = true;
    return;
  }
  // Persist the bypass pair (this machine only) before reloading into the shop.
  setDevStoreBypass(bypass.value.trim());
  setDevStoreOverride(id);
  location.reload();
}

function reset() {
  clearDevStoreOverride();
  location.reload();
}
</script>

<template>
  <div class="dsp-root" :class="{ 'dsp-on': isImpersonating }">
    <!-- collapsed pill -->
    <button v-if="!open" class="dsp-pill" @click="open = true">
      <span class="dsp-dot" />
      <span class="dsp-pill-label">
        {{ isImpersonating ? `shop ${effectiveStoreId}` : "DEV store" }}
      </span>
    </button>

    <!-- expanded panel -->
    <div v-else class="dsp-panel">
      <div class="dsp-head">
        <span class="dsp-title">DEV · Store override</span>
        <button class="dsp-x" @click="open = false">✕</button>
      </div>

      <div class="dsp-meta">
        <div>
          <span class="dsp-k">Viewing</span>
          <span class="dsp-v" :class="{ 'dsp-v-on': isImpersonating }">
            {{ effectiveStoreId || "—" }}
          </span>
        </div>
        <div v-if="isImpersonating">
          <span class="dsp-k">Account</span>
          <span class="dsp-v dsp-v-muted">{{ realStoreId || "—" }}</span>
        </div>
      </div>

      <form class="dsp-form" @submit.prevent="apply">
        <input
          v-model="input"
          class="dsp-input"
          type="text"
          inputmode="numeric"
          placeholder="Customer ID (e.g. 5422)"
          autofocus
        />
        <button class="dsp-apply" type="submit">Switch</button>
      </form>

      <input
        v-model="bypass"
        class="dsp-input dsp-bypass"
        type="text"
        placeholder="bypass pair — key=value"
        spellcheck="false"
        autocomplete="off"
        :class="{ 'dsp-input-err': bypassError }"
        @input="bypassError = false"
      />
      <p v-if="bypassError" class="dsp-hint dsp-hint-err">
        Required — set the server's bypass pair as key=value.
      </p>
      <p v-else class="dsp-hint">
        Required · stored on this machine only.
      </p>

      <button v-if="isImpersonating" class="dsp-reset" @click="reset">
        ← Back to my shop ({{ realStoreId || "real" }})
      </button>

      <p class="dsp-note">Reloads to apply · dev build only</p>
    </div>
  </div>
</template>

<style scoped>
.dsp-root {
  position: fixed;
  right: 12px;
  bottom: 12px;
  z-index: 2147483000;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  font-size: 12px;
}

/* collapsed pill */
.dsp-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border: none;
  border-radius: 999px;
  background: #5b1a6b; /* --nl-purple-700 */
  color: #fff;
  cursor: pointer;
  opacity: 0.55;
  transition: opacity 0.12s;
}
.dsp-pill:hover { opacity: 1; }
.dsp-on .dsp-pill { background: #b3261e; opacity: 0.92; } /* danger red when impersonating */
.dsp-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #fff;
}
.dsp-pill-label { font-weight: 600; letter-spacing: 0.02em; }

/* expanded panel */
.dsp-panel {
  width: 230px;
  padding: 12px;
  border-radius: 10px;
  background: #1c1420;
  color: #f3eef6;
  border: 1px solid #5b1a6b;
  box-shadow: 0 8px 28px rgba(0, 0, 0, 0.4);
}
.dsp-on .dsp-panel { border-color: #b3261e; }

.dsp-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.dsp-title { font-weight: 700; font-size: 11px; letter-spacing: 0.04em; text-transform: uppercase; color: #cba7d8; }
.dsp-x {
  border: none;
  background: transparent;
  color: #8f7d99;
  cursor: pointer;
  font-size: 12px;
  padding: 0 2px;
}
.dsp-x:hover { color: #fff; }

.dsp-meta {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-bottom: 10px;
  padding: 7px 9px;
  border-radius: 7px;
  background: rgba(255, 255, 255, 0.05);
}
.dsp-meta > div { display: flex; justify-content: space-between; align-items: baseline; }
.dsp-k { color: #9a8aa3; }
.dsp-v { font-weight: 700; font-variant-numeric: tabular-nums; }
.dsp-v-on { color: #ff8a80; }
.dsp-v-muted { font-weight: 500; color: #9a8aa3; }

.dsp-form { display: flex; gap: 6px; }
.dsp-input {
  flex: 1;
  min-width: 0;
  padding: 7px 9px;
  border-radius: 7px;
  border: 1px solid #3a2d42;
  background: #120d15;
  color: #fff;
  font-size: 12px;
  outline: none;
}
.dsp-input:focus { border-color: #9747c7; }
.dsp-apply {
  padding: 7px 12px;
  border: none;
  border-radius: 7px;
  background: #7e2d9c; /* --nl-purple-600 */
  color: #fff;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;
}
.dsp-apply:hover { background: #9747c7; }

.dsp-bypass {
  width: 100%;
  margin-top: 6px;
  font-size: 11px;
}
.dsp-hint {
  margin: 4px 0 0;
  font-size: 10px;
  color: #6f6178;
}
.dsp-hint-err { color: #ff8a80; }
.dsp-input-err { border-color: #b3261e; }

.dsp-reset {
  width: 100%;
  margin-top: 8px;
  padding: 6px;
  border: 1px solid #3a2d42;
  border-radius: 7px;
  background: transparent;
  color: #cba7d8;
  cursor: pointer;
  font-size: 11px;
}
.dsp-reset:hover { background: rgba(255, 255, 255, 0.06); }

.dsp-note {
  margin: 8px 0 0;
  font-size: 10px;
  color: #6f6178;
  text-align: center;
}
</style>
